import React, { useState, memo, useMemo, useEffect } from "react";
import { Select, Button, Input } from 'antd';
import utils from "../../dependencies/custom/react-utilities";
const tables = ["users", "orders", "products"];

const fields = {
    users: ["id", "name", "email"],
    orders: ["id", "user_id", "amount"],
    products: ["id", "name", "price"],
};

const tableKeys = {
    users: ["id"],
    orders: ["id", "user_id"],
    products: ["id", "order_id"],
};

// const conditions = ["=", ">", "<", "IN", "EXISTS"];
const SQL_VALUE_PLACEHOLDER = 'MYVAL';

const conditions = [
    { label: '=', value: `= ${SQL_VALUE_PLACEHOLDER}` },
    { label: '!=', value: `!= ${SQL_VALUE_PLACEHOLDER}` },
    { label: '<', value: `< ${SQL_VALUE_PLACEHOLDER}` },
    { label: '>', value: `> ${SQL_VALUE_PLACEHOLDER}` },
    { label: '<=', value: `<= ${SQL_VALUE_PLACEHOLDER}` },
    { label: '>=', value: `>= ${SQL_VALUE_PLACEHOLDER}` },
    { label: 'contain', value: `LIKE %${SQL_VALUE_PLACEHOLDER}%` },
    { label: 'does not contain', value: `NOT LIKE %${SQL_VALUE_PLACEHOLDER}%` },
    { label: 'begins with', value: `LIKE ${SQL_VALUE_PLACEHOLDER}%` },
    { label: 'ends with', value: `LIKE %${SQL_VALUE_PLACEHOLDER}` },
    { label: 'does not begins with', value: `NOT LIKE ${SQL_VALUE_PLACEHOLDER}%` },
    { label: 'does not ends with', value: `NOT LIKE %${SQL_VALUE_PLACEHOLDER}` },
    { label: 'is null', value: 'IS NULL' },
    { label: 'is not null', value: 'IS NOT NULL' },
    { label: 'is null-safe', value: '<=> NULL' },
    { label: 'is not null-safe', value: 'IS NOT <=> NULL' },
    { label: 'in', value: `IN (${SQL_VALUE_PLACEHOLDER})` },
    { label: 'not in', value: `NOT IN (${SQL_VALUE_PLACEHOLDER})` },
    // { label: 'between', value: 'BETWEEN val1 AND val2' },
    // { label: 'not between', value: 'NOT BETWEEN val1 AND val2' },
];

const joinTypes = ["INNER JOIN", "LEFT JOIN", "RIGHT JOIN", "FULL JOIN"];
const aggregateFunctions = ["SUM", "COUNT", "AVG", "MIN", "MAX"];
const orderOptions = ["ASC", "DESC"];
const logicalOperator = ['AND', 'OR'];
const DynamicQueryBuilder = () => {
    const [joins, setJoins] = useState([]);
    const [filters, setFilters] = useState([]);
    const [groupBy, setGroupBy] = useState("");
    const [orderBy, setOrderBy] = useState("");
    const [orderType, setOrderType] = useState("ASC");
    const [aggregateSelections, setAggregateSelections] = useState([]);
    const [havingConditions, setHavingConditions] = useState([]);
    const [selectFields, setSelectFields] = useState([]);
    const [selectedTables, setSelectedTables] = useState([]);
    const filtersMeta = { table: null, field: null, operator: null, value: null, logicalOperator: null, isSubQuery: false, subQueryTable: null, subQueryField: null, parentId: null, id: null };
    const tableAsOptions = selectedTables?.map(v => ({ value: v, label: v }));
    const logOpAsOptions = logicalOperator?.map(v => ({ value: v, label: v }));
    const joinTypeOptions = useMemo(() => {
        return joinTypes.map(type => ({ value: type, label: type }));
    }, []);

    function parseOperator(operator, value) {
        switch (operator) {
            case `LIKE %${SQL_VALUE_PLACEHOLDER}%`:
            case `NOT LIKE %${SQL_VALUE_PLACEHOLDER}%`: {
                return operator?.replace(`%${SQL_VALUE_PLACEHOLDER}%`, `'%${value}%'`);
            }
            case `LIKE ${SQL_VALUE_PLACEHOLDER}%`:
            case `NOT LIKE ${SQL_VALUE_PLACEHOLDER}%`: {
                return operator?.replace(`${SQL_VALUE_PLACEHOLDER}%`, `'${value}%'`);
            }
            case `LIKE %${SQL_VALUE_PLACEHOLDER}`:
            case `NOT LIKE %${SQL_VALUE_PLACEHOLDER}`: {
                return operator?.replace(`%${SQL_VALUE_PLACEHOLDER}`, `'%${value}'`);
            }
            case `IN (${SQL_VALUE_PLACEHOLDER})`:
            case `NOT IN (${SQL_VALUE_PLACEHOLDER})`: {
                return operator?.replace(SQL_VALUE_PLACEHOLDER, utils.genSqlIn(value?.split(',')));
            }
            default: {
                const formattedValue =
                    typeof value === 'string'
                        ? `'${value}'`
                        : value;
                return operator?.replace(SQL_VALUE_PLACEHOLDER, formattedValue);
            }

        }
    }

    const handleChange = (index, key, value, stateArray, setFunction) => {
        const updatedArray = stateArray.map((item, i) =>
            i === index ? { ...item, [key]: value } : item
        );
        setFunction(updatedArray);
    };

    const fieldOptions = useMemo(() => {
        let fdx = [];
        for (let table in fields) {
            const f = fields[table];
            for (let item of f) {
                fdx.push({ value: `${table}.${item}`, label: `${table}[${item}]` });
            }
        }
        return fdx;
    }, [fields]); // Dependencies: Only recompute when `fields` changes.



    const memoizedSelect = useMemo(() => {
        return <>
            <Select
                mode="multiple"
                placeholder="Select fields"
                className="bd-highlight flex-grow-1x w-50"
                onChange={v => handleChangeFields(v)}
                value={selectFields}
                options={fieldOptions}
                showSearch
                filterOption={selectFilterOption}
            />
        </>

    }, [fieldOptions, selectFields]); // Dependencies: Recompute if `fieldOptions` or `selectFields` changes.



    useEffect(() => {
        const st = tablesToJoin();
        setSelectedTables(st);
        const numbJoins = st.length - 1;
        const joinModel = Array.from({ length: numbJoins }, () => ({
            type: "INNER JOIN",
            table1: null,
            table2: null,
            table1Field: null,
            table2Field: null
        }));
        setJoins(joinModel);
        console.log(filters)
    }, [selectFields, /*filters*/]);


    function renderFilters(parentId = null) {
        const children = filters.filter(f => f.parentId === parentId);
        return children.map(filter => (
            <div key={filter.id} style={{ marginLeft: parentId ? 20 : 0, borderLeft: '1px solid #ccc', paddingLeft: 10 }}>
                <Select
                    placeholder="Select Table"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => updateFilter(filter.id, "table", v)}
                    value={filter?.table}
                    options={tableAsOptions}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Select
                    placeholder="Select Field"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => updateFilter(filter.id, "field", v)}
                    value={filter?.field}
                    options={fields[filter?.table]?.map(v => ({ label: v, value: v }))}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Select
                    placeholder="Select Operator"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => updateFilter(filter.id, "operator", v)}
                    value={filter?.operator}
                    options={conditions}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Input
                    placeholder="Type Value"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => updateFilter(filter.id, "value", v.target.value)}
                    value={filter?.value}
                />
                <Select
                    placeholder="Select Operator"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => updateFilter(filter.id, "logicalOperator", v)}
                    value={filter?.logicalOperator}
                    options={logOpAsOptions}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Button size="small" onClick={() => addFilter(filter.id)}>+ Group</Button>
                <Button size="small" style={{ color: 'red' }} onClick={() => removeFilter(filter.id)}>x Remove</Button>

                {/* Recursive rendering of children */}
                {renderFilters(filter.id)}
            </div>
        ));
    }

    function removeFilter(id) {
        let childrenFilters = [];
        function getChildrenFilters(id) {
            const children = filters.filter((v) => v.parentId == id);
            for (let child of children) {
                getChildrenFilters(child.id);
            }
            childrenFilters.push(...children);
        }
        getChildrenFilters(id);
        let newFilters = filters?.filter((v) => v.id != id);//remove the clicked element from the filters. this return all elements other than the clicked element        
        for (let child of childrenFilters) {
            const childId = child.id;
            newFilters = newFilters?.filter((v) => v.id != childId);
        }

        setFilters(newFilters);
    }

    function updateFilter(id, key, value) {
        setFilters(prev =>
            prev.map(f => (f.id === id ? { ...f, [key]: value } : f))
        );
    }

    function addFilter(parentId = null) {
        setFilters(prev => [
            ...prev,
            { ...filtersMeta, parentId, id: utils.generateUuid() }
        ]);
    }


    function generateJoinForms() {
        return joins.map((join, i) => (
            <div key={utils.generateUuid()} className="mb-2">
                <Select
                    placeholder="Select Join Type"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => handleChange(i, "type", v, joins, setJoins)}
                    value={joins[i]?.type}
                    options={joinTypeOptions}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Select
                    placeholder="Select Table1"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => handleChange(i, "table1", v, joins, setJoins)}
                    value={joins[i]?.table1}
                    options={tableAsOptions}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Select
                    placeholder="Select Table1 Field"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => handleChange(i, "table1Field", v, joins, setJoins)}
                    value={joins[i]?.table1Field}
                    options={tableKeys[joins[i]?.table1]?.map(v => ({ value: `${joins[i]?.table1}.${v}`, label: `${joins[i]?.table1}[${v}]` }))}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Select
                    placeholder="Select Table2"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => handleChange(i, "table2", v, joins, setJoins)}
                    value={joins[i]?.table2}
                    options={tableAsOptions}
                    showSearch
                    filterOption={selectFilterOption}
                />
                <Select
                    placeholder="Select Table2 Field"
                    className="bd-highlight w-25x"
                    style={{ width: '10rem' }}
                    onChange={v => handleChange(i, "table2Field", v, joins, setJoins)}
                    value={joins[i]?.table2Field}
                    options={tableKeys[joins[i]?.table2]?.map(v => ({ value: `${joins[i]?.table2}.${v}`, label: `${joins[i]?.table2}[${v}]` }))}
                    showSearch
                    filterOption={selectFilterOption}
                />
            </div>
        ));
    }

    function selectFilterOption(input, option) {
        return (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
    }

    function handleChangeFields(v) {
        setSelectFields(v);
    }


    const handleAddFilter = () => {
        setFilters([...filters, { ...filtersMeta, id: utils.generateUuid() }]);
    };

    function tablesToJoin() {
        let t = new Set();
        selectFields?.forEach(v => {
            const tbl = v?.split('.')[0];
            t.add(tbl);
        });
        return Array.from(t);
    }

    const generateSQLQuery = () => {
        const FROM = `FROM ${selectedTables.length ? selectedTables[0] : ''}`;
        const JOIN = generateJoinSql();
        const FILTERS = generateFilterSql(filters);
        // console.log(FILTERS)
        let SQL = selectFields.length ? `SELECT ${selectFields.join(",")} ${FROM} ${JOIN} ${FILTERS ? ` WHERE ${FILTERS}` : ''}` : '';
        return SQL;
    }

  

    function trimChar(str, charToRemove) {
        // Remove from beginning
        if (str.startsWith(charToRemove)) {
          str = str.substring(1);
        }
      
        // Remove from end
        if (str.endsWith(charToRemove)) {
          str = str.substring(0, str.length - 1);
        }
      
        return str;
      }
      

    function generateFilterSql() {
        function buildWhereClause(parentId = null) {
            const children = filters.filter(f => f.parentId === parentId);
            if (!children.length) return '';
            return children
                .map((filter, index) => {
                    const { table, field, operator, value, id, logicalOperator } = filter;
                    const v = parseOperator(operator, value);
                    const baseCondition = `${table}.${field} ${v}`;
                    const nested = buildWhereClause(id);
                    const fullCondition = nested ? `(${baseCondition} AND (${nested}))` : baseCondition;
                    const logic = filter.logicalOperator ? filter.logicalOperator : '';
                    // return trimChar(trimChar(` ${fullCondition} ${logic} `, 'AND'), 'OR');
                    return ` ${fullCondition} ${logic} `;
                })
                .join(' ');
        }

        const SQL = buildWhereClause();
        return SQL;
    }



    function generateJoinSql() {
        let SQL = '';
        //using this to get how the user has joined the tables which includes the order or how the tables have been sequenced
        const tablesInJoin = new Set();
        for (let j of joins) {
            tablesInJoin.add(j.table1);
            tablesInJoin.add(j.table2);
        }
        //we don't want the first table to be part of the join statement because it's automatically added to the FROM statement
        //we don't want any of the tables to be null as well
        //the number of joins must be equal to the number of the tables to join on
        const tables = Array.from(tablesInJoin)?.filter(v => v != selectedTables[0] && v != null);

        if (tables.length == joins.length) {
            for (let i = 0; i < joins.length; i++) {
                const join = joins[i];
                const currentTableToJoin = tables[i];
                SQL += ` ${join.type} ${currentTableToJoin} ON ${join.table1Field} = ${join.table2Field} `;
            }
        }
        return SQL;
    }

    return (
        <>
            {/* {console.log(joins)} */}
            <h3>Fields</h3>
            {memoizedSelect}
            <br /><br />
            <h3>FILTERS</h3>
            {renderFilters(null)}
            <Button onClick={handleAddFilter}>+ Add Filter</Button>
            <br /><br />
            <h3>JOINS</h3>
            {generateJoinForms()}
            <h3>Generated SQL Query</h3>
            <pre style={{ whiteSpace: "pre-wrap", background: "#f4f4f4", padding: "10px" }}>
                {generateSQLQuery()}
            </pre>
        </>
    );



}

export default memo(DynamicQueryBuilder);